import { useState } from "react";
import { BRANDS } from "@/lib/brands";
import { useLang } from "@/lib/i18n";

const CATEGORY_KEYS = ["All", "Payments", "SaaS", "Platform", "POS", "CRM", "Marketing", "Security", "Agency", "Theme"];

export default function Brands() {
  const { lang, t } = useLang();
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? BRANDS : BRANDS.filter((b) => b.category === filter);
  const cat = (key) => t(`brands.categories.${key}`);

  return (
    <section id="brands" data-testid="brands-section" className="py-24 lg:py-32 relative">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="pill"><i className="fa-solid fa-grid" /> {t("brands.pill")}</span>
            <h2 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-[64px] leading-[1.0] tracking-tight max-w-[16ch]">
              {t("brands.titleLead")}
              <em className="italic font-light text-[hsl(var(--accent))]">{t("brands.titleHighlight")}</em>
            </h2>
            <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-[hsl(var(--ink-soft))]">
              {t("brands.paragraph")}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 max-w-2xl">
            {CATEGORY_KEYS.map((c) => (
              <button
                key={c}
                data-testid={`brand-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`text-xs font-medium px-3.5 py-2 rounded-full border transition-colors ${
                  filter === c
                    ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))] border-[hsl(var(--ink))]"
                    : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
                }`}
              >
                {cat(c)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((b, i) => (
            <a
              key={b.name}
              href={b.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`brand-card-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="brand-tile soft-card p-7 group flex flex-col justify-between min-h-[260px] fade-up"
              style={{ "--brand": b.color, animationDelay: `${i * 50}ms` }}
            >
              <div className="brand-glow" />
              <div className="relative z-10 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-lg"
                    style={{ background: `${b.color}1a`, color: b.color }}
                  >
                    <i className={`fa-solid ${b.icon}`} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--muted-foreground))]">
                      {b.n} · {cat(b.category)}
                    </div>
                    <div className="font-display font-semibold text-xl tracking-tight">{b.name}</div>
                  </div>
                </div>
                <i className="fa-solid fa-arrow-up-right-from-square text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--ink))] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
              </div>

              <div className="relative z-10 mt-5">
                <div className="text-sm font-semibold mb-2" style={{ color: b.color }}>
                  {b.tagline[lang]}
                </div>
                <p className="text-[13.5px] leading-relaxed text-[hsl(var(--ink-soft))]">
                  {b.desc[lang]}
                </p>
              </div>

              <div className="relative z-10 mt-5 flex items-center justify-between">
                <span className="text-xs font-mono" style={{ color: b.color }}>
                  {new URL(b.url).host}
                </span>
                <span className="text-xs font-medium link-underline" style={{ color: b.color }}>
                  {t("brands.visitSite")}
                  <i className="fa-solid fa-arrow-right" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
